import { useEffect, useMemo, useState } from 'react'

import { useDebouncedValue } from '@/features/discovery/hooks/ui'

import { chatApi, type ActivityProjection, type Friend } from '../api'

export type ChatSearchPhase = 'idle' | 'loading' | 'ready' | 'error'

export interface ChatSearchState {
  phase: ChatSearchPhase
  term: string
  friends: Friend[]
  activities: ActivityProjection[]
  error: unknown
}

interface SearchResult {
  term: string
  phase: Exclude<ChatSearchPhase, 'idle'>
  friends: Friend[]
  activities: ActivityProjection[]
  error: unknown
}

const MIN_QUERY_LENGTH = 2
const EMPTY_RESULT: SearchResult = {
  term: '',
  phase: 'loading',
  friends: [],
  activities: [],
  error: null,
}

function matchesFriend(friend: Friend, normalized: string): boolean {
  return (
    friend.nickname.toLocaleLowerCase().includes(normalized) ||
    friend.publicName.toLocaleLowerCase().includes(normalized)
  )
}

/**
 * Chats search matches UI_PARITY §3.12: friends and activities, minimum two
 * characters, 300 ms debounce. Friends are filtered from the frozen
 * `GET /friends` list (accepted friendships only); activities use the exact
 * discovery query so the server applies the same visibility rules.
 *
 * `idle`/`loading` are derived from the current term instead of set inside the
 * effect, so a short query never causes a cascading render.
 */
export function useChatSearch(query: string): ChatSearchState {
  const debounced = useDebouncedValue(query, 300)
  const term = debounced.trim()
  const [result, setResult] = useState<SearchResult>(EMPTY_RESULT)

  useEffect(() => {
    if (term.length < MIN_QUERY_LENGTH) return
    const controller = new AbortController()

    Promise.all([
      chatApi.listFriends({ signal: controller.signal }),
      chatApi.searchActivities(term, { signal: controller.signal }),
    ])
      .then(([friendList, activityList]) => {
        if (controller.signal.aborted) return
        const normalized = term.toLocaleLowerCase().replace(/^@/, '')
        setResult({
          term,
          phase: 'ready',
          friends: friendList.friends.filter((item) =>
            matchesFriend(item, normalized),
          ),
          activities: activityList.items,
          error: null,
        })
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setResult({
          term,
          phase: 'error',
          friends: [],
          activities: [],
          error: cause,
        })
      })

    return () => controller.abort()
  }, [term])

  return useMemo(() => {
    if (term.length < MIN_QUERY_LENGTH) {
      return {
        phase: 'idle',
        term,
        friends: [],
        activities: [],
        error: null,
      }
    }
    if (result.term !== term || result.phase === 'loading') {
      return {
        phase: 'loading',
        term,
        friends: [],
        activities: [],
        error: null,
      }
    }
    return {
      phase: result.phase,
      term,
      friends: result.friends,
      activities: result.activities,
      error: result.error,
    }
  }, [result, term])
}
